"use client"

/**
 * Question composer — the field-level UI the builder and inspector are made of.
 *
 * Split out of `new-library-item-form.tsx` alongside `composer-form.ts` so both
 * hosts of the composer body render the same controls. Nothing here owns form
 * state: every piece takes a value and a change handler.
 */

import * as React from "react"

import { cn } from "@/lib/utils"
import { PBI_EXPLAINER, pbiBand } from "@/lib/pbi-bands"
import {
  ICON_DISC_TONE_CLASS,
  type TintedIconDiscTone,
} from "@/components/tinted-icon-disc"

import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@/components/ui/input-group"
import { Separator } from "@/components/ui/separator"
import { Label } from "@/components/ui/label"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { Kbd } from "@/components/ui/kbd"
import { Textarea } from "@/components/ui/textarea"
import { Tip } from "@/components/ui/tip"
import { FilterChipGroup } from "@/components/ui/filter-chip-group"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"
import { ToggleSwitch } from "@/components/ui/toggle-switch"
import { ComposerFieldSelectionAsk } from "./composer-selection-ask"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  BUILDER_LIST_FIELD,
  BUILDER_LIST_LOCKED,
  BUILDER_LIST_ROW,
  BuilderAnswerChip,
  BuilderListRow,
  BuilderListRowActions,
  BuilderSection,
  builderListIndexClass,
} from "@/components/question-builder-primitives"
import { LibraryFolderPickerPanel } from "@/components/library-folder-picker-panel"
import {
  AUTHORING_BLOOM_OPTIONS,
  AUTHORING_DIFFICULTY_OPTIONS,
  AUTHORING_REFERENCE_KIND_OPTIONS,
  authoringQuestionType,
  type AuthoringQuestionType,
  type AuthoringQuestionTypeOption,
  type AuthoringReference,
  type AuthoringReferenceKind,
} from "@/lib/library-authoring"
import type { LibraryBloomLevel } from "@/lib/mock/library"
import type {
  LibraryFolder,
  LibraryFolderColorKey,
} from "@/lib/mock/library-folders"

import {
  difficultyInsightForFolder,
  folderBreadcrumb,
} from "./composer-form"
import { FillBlankRichTextEditor } from "./fill-blank-rich-text-editor"

export { COMPOSER_RICH_TEXT_EDITOR_SHELL_CLASS } from "./composer-input-chrome"

/** Shared with `Input`, tags shell, marks stepper — `border-input` for UI contrast (WCAG 1.4.11). */
const COMPOSER_INPUT_SHELL_BORDER_CLASS =
  "rounded-md border border-input bg-transparent transition-colors dark:bg-input-background"

const COMPOSER_INPUT_SHELL_FOCUS_WITHIN_CLASS =
  "focus-within:border-ring focus-within:ring-3 focus-within:ring-ring/50"

/** Shared trigger chrome for inspector pickers (Locations, Question format). */
export const INSPECTOR_PICKER_TRIGGER_CLASS = cn(
  COMPOSER_INPUT_SHELL_BORDER_CLASS,
  "group flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-muted/40",
  "focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none",
  "disabled:cursor-not-allowed disabled:opacity-50",
)

/** Shared inspector grouping — matches the Discrimination {@link DifficultyMeter} card. */
export const INSPECTOR_SECTION_CARD_CLASS =
  "flex flex-col gap-3 rounded-lg border border-border bg-muted/20 p-3"

export function InspectorSectionCard({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <div className={cn(INSPECTOR_SECTION_CARD_CLASS, className)}>{children}</div>
  )
}

export function InspectorToggleRow({
  id,
  label,
  description,
  checked,
  onCheckedChange,
  disabled,
  variant = "card",
}: {
  id: string
  label: string
  /** Only for toggles whose label does not say what happens to the score. */
  description?: string
  checked: boolean
  onCheckedChange: (next: boolean) => void
  disabled?: boolean
  /** `card` = standalone bordered row; `inset` = row inside {@link InspectorSectionCard}. */
  variant?: "card" | "inset"
}) {
  if (variant === "inset") {
    return (
      <div
        className={cn(
          "flex items-start justify-between gap-3",
          disabled && "opacity-60",
        )}
      >
        <div className="min-w-0 flex-1 space-y-0.5">
          <Label
            htmlFor={id}
            className="text-sm leading-snug font-medium text-foreground"
          >
            {label}
          </Label>
          {description ? (
            <p className="text-xs leading-snug text-muted-foreground">
              {description}
            </p>
          ) : null}
        </div>
        <ToggleSwitch
          id={id}
          checked={checked}
          onChange={onCheckedChange}
          disabled={disabled}
        />
      </div>
    )
  }

  return (
    <div
      className={cn(
        "flex items-start justify-between gap-3 rounded-lg border border-border bg-muted/10 px-3 py-2.5",
        disabled && "opacity-60",
      )}
    >
      <div className="min-w-0 flex-1 space-y-0.5">
        <Label
          htmlFor={id}
          className="text-sm leading-snug font-medium text-foreground"
        >
          {label}
        </Label>
        {description ? (
          <p className="text-xs leading-snug text-muted-foreground">
            {description}
          </p>
        ) : null}
      </div>
      <ToggleSwitch
        id={id}
        checked={checked}
        onChange={onCheckedChange}
        disabled={disabled}
      />
    </div>
  )
}

export function InspectorSection({
  title,
  htmlFor,
  children,
  description,
  actions,
}: {
  title: string
  htmlFor?: string
  children: React.ReactNode
  description?: React.ReactNode
  actions?: React.ReactNode
}) {
  if (htmlFor) {
    return (
      <section className="flex flex-col gap-2">
        <Label
          htmlFor={htmlFor}
          className="text-sm font-semibold text-foreground"
        >
          {title}
        </Label>
        {children}
        {description ? (
          <p className="text-xs leading-snug text-muted-foreground">
            {description}
          </p>
        ) : null}
      </section>
    )
  }

  return (
    <BuilderSection title={title} actions={actions} description={description}>
      {children}
    </BuilderSection>
  )
}

function parseMarksValue(value: string, fallback = 1): number {
  const trimmed = value.trim()
  if (!trimmed) return fallback
  const n = Number.parseFloat(trimmed)
  return Number.isFinite(n) ? n : fallback
}

/** Segmented `[- | value | +]` marks control — `InputGroup` shell + dividers. */
export function MarksInputStepper({
  id,
  value,
  placeholder = "1",
  disabled,
  onChange,
  className,
}: {
  id: string
  value: string
  placeholder?: string
  disabled?: boolean
  onChange: (next: string) => void
  className?: string
}) {
  const bumpMarks = (delta: number) => {
    const current = parseMarksValue(value, parseMarksValue(placeholder))
    const next = Math.max(0, current + delta)
    onChange(next <= 0 ? "" : String(next))
  }

  const stepperDividerClass =
    "h-auto shrink-0 self-stretch bg-border data-[orientation=vertical]:h-auto data-[orientation=vertical]:w-px"

  return (
    <InputGroup
      aria-label="Marks"
      className={cn(
        "h-9 w-auto shrink-0 overflow-hidden",
        disabled && "pointer-events-none opacity-50",
        className,
      )}
    >
      <InputGroupAddon align="inline-start" className="!ms-0 p-0 ps-0">
        <Tooltip>
          <TooltipTrigger asChild>
            <InputGroupButton
              type="button"
              size="icon-sm"
              aria-label="Decrease marks"
              disabled={disabled}
              onClick={() => bumpMarks(-1)}
              className="size-9 rounded-none bg-transparent text-base shadow-none hover:bg-muted/60"
            >
              <i className="fa-light fa-minus text-base" aria-hidden="true" />
            </InputGroupButton>
          </TooltipTrigger>
          <TooltipContent side="top">Decrease marks</TooltipContent>
        </Tooltip>
      </InputGroupAddon>
      <Separator
        orientation="vertical"
        decorative
        className={stepperDividerClass}
      />
      <InputGroupInput
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        inputMode="decimal"
        placeholder={placeholder}
        disabled={disabled}
        aria-label="Marks"
        className="w-10 min-w-0 max-w-10 border-0 px-0.5 text-center text-lg font-semibold tabular-nums shadow-none ring-0 focus-visible:border-transparent focus-visible:ring-0"
      />
      <Separator
        orientation="vertical"
        decorative
        className={stepperDividerClass}
      />
      <InputGroupAddon align="inline-end" className="!me-0 p-0 pe-0">
        <Tooltip>
          <TooltipTrigger asChild>
            <InputGroupButton
              type="button"
              size="icon-sm"
              aria-label="Increase marks"
              disabled={disabled}
              onClick={() => bumpMarks(1)}
              className="size-9 rounded-none bg-transparent text-base shadow-none hover:bg-muted/60"
            >
              <i className="fa-light fa-plus text-base" aria-hidden="true" />
            </InputGroupButton>
          </TooltipTrigger>
          <TooltipContent side="top">Increase marks</TooltipContent>
        </Tooltip>
      </InputGroupAddon>
    </InputGroup>
  )
}

const COMPOSER_TAGS_FIELD_SHELL = cn(
  COMPOSER_INPUT_SHELL_BORDER_CLASS,
  COMPOSER_INPUT_SHELL_FOCUS_WITHIN_CLASS,
  "flex min-w-0 flex-wrap items-center gap-1.5 overflow-hidden px-2.5 py-1.5 has-disabled:opacity-50",
)

/** Tags composer — tags + draft input in one wrapping row. */
export function ComposerTagsField({
  id,
  tags,
  draft,
  disabled,
  onDraftChange,
  onCommit,
  onRemove,
}: {
  id: string
  tags: string[]
  draft: string
  disabled?: boolean
  onDraftChange: (next: string) => void
  onCommit: () => void
  onRemove: (tag: string) => void
}) {
  return (
    <div
      className={COMPOSER_TAGS_FIELD_SHELL}
      role={tags.length > 0 ? "list" : undefined}
      aria-label={tags.length > 0 ? "Added tags" : undefined}
    >
      {tags.length > 0
        ? tags.map((tag) => (
            <Badge
              key={tag}
              variant="secondary"
              asChild
              role="listitem"
              className="h-auto max-w-full min-w-0 shrink whitespace-normal px-1.5 py-0.5 text-xs font-medium leading-snug"
            >
              <button
                type="button"
                onClick={() => onRemove(tag)}
                aria-label={`Remove tag ${tag}`}
                disabled={disabled}
                className="flex max-w-full min-w-0 items-start gap-1 text-left"
              >
                <span className="min-w-0 break-all">#{tag}</span>
                <i
                  className="fa-light fa-xmark mt-0.5 shrink-0"
                  data-icon="inline-end"
                  aria-hidden="true"
                />
              </button>
            </Badge>
          ))
        : null}
      <input
        id={id}
        value={draft}
        disabled={disabled}
        onChange={(event) => onDraftChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === ",") {
            event.preventDefault()
            onCommit()
          }
        }}
        onBlur={onCommit}
        placeholder={tags.length > 0 ? "Add another tag…" : "STEMI, antibiotics…"}
        aria-label="Add tags"
        className={cn(
          "min-h-6 border-0 bg-transparent p-0 text-xs text-foreground outline-none",
          "placeholder:text-muted-foreground disabled:cursor-not-allowed md:text-sm",
          tags.length > 0 ? "min-w-[6rem] flex-1 basis-20" : "w-full min-w-0 flex-1",
        )}
      />
    </div>
  )
}

/** Field labels on the composer builder + inspector (Title, Question, Marks, …). */
export const COMPOSER_FIELD_LABEL_CLASS =
  "text-xs font-medium text-muted-foreground"

/** Subsection label inside inspector cards (Difficulty, Discrimination, …). */
export function InspectorSubsectionLabel({
  children,
  htmlFor,
}: {
  children: React.ReactNode
  htmlFor?: string
}) {
  return (
    <Label
      htmlFor={htmlFor}
      className="text-xs font-medium text-muted-foreground"
    >
      {children}
    </Label>
  )
}

/** Builder canvas — format, stem, references, type-specific blocks, rationale.
 *  Uncarded so the page dot field reads through; fields keep their own surfaces. */
export function QuestionBuilderCard({
  children,
  pageScroll = false,
}: {
  children: React.ReactNode
  pageScroll?: boolean
}) {
  if (pageScroll) {
    return (
      <div className="w-full min-w-0">
        <div className="flex flex-col gap-4 px-1 pt-0 pb-3 sm:px-2 sm:pt-0 sm:pb-3">
          {children}
        </div>
      </div>
    )
  }

  return (
    <div className="flex max-h-full min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden">
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain px-1 py-1 pb-3 [-webkit-overflow-scrolling:touch] sm:px-2 sm:py-1 sm:pb-3">
        <div className="flex flex-col gap-4">{children}</div>
      </div>
    </div>
  )
}

/** Past this the stem scrolls internally instead of pushing the card body away. */
const LEAD_IN_MAX_HEIGHT_PX = 384

/**
 * Textarea that grows with its content. The stem field used to trap long
 * questions in a 3-row box: the native resize handle stalls as soon as it
 * reaches the bottom of the card's scroll viewport, because the card does not
 * auto-scroll mid-drag — so authors could not open the field up. Autosizing
 * follows `AskLeoComposer` (measure `scrollHeight`, no `field-sizing`), caps at
 * `maxHeight`, and steps aside once the author sets a height by hand.
 */
export function AutosizeTextarea({
  maxHeight = LEAD_IN_MAX_HEIGHT_PX,
  ref,
  ...props
}: React.ComponentProps<typeof Textarea> & { maxHeight?: number }) {
  const elRef = React.useRef<HTMLTextAreaElement | null>(null)
  const appliedInlineHeightRef = React.useRef<string | null>(null)
  const widthRef = React.useRef<number | null>(null)
  const authorResizedRef = React.useRef(false)
  const observedRef = React.useRef<HTMLTextAreaElement | null>(null)
  const observerRef = React.useRef<ResizeObserver | null>(null)

  const fit = React.useCallback(() => {
    const el = elRef.current
    if (!el || authorResizedRef.current) return
    el.style.height = "auto"
    el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`
    el.style.overflowY = el.scrollHeight > maxHeight ? "auto" : "hidden"
    appliedInlineHeightRef.current = el.style.height
    widthRef.current = el.offsetWidth
  }, [maxHeight])

  const bindTextareaRef = React.useCallback(
    (el: HTMLTextAreaElement | null) => {
      observerRef.current?.disconnect()
      observerRef.current = null
      elRef.current = el

      if (el) {
        authorResizedRef.current = false
        observedRef.current = el
        fit()

        if (typeof ResizeObserver !== "undefined") {
          const observer = new ResizeObserver(() => {
            if (el.offsetWidth !== widthRef.current) {
              fit()
              return
            }
            if (el.style.height !== appliedInlineHeightRef.current) {
              authorResizedRef.current = true
            }
          })
          observer.observe(el)
          observerRef.current = observer
        }
      } else {
        observedRef.current = null
      }

      if (typeof ref === "function") ref(el)
      else if (ref) ref.current = el
    },
    [fit, ref],
  )

  React.useLayoutEffect(() => {
    fit()
  })

  React.useEffect(
    () => () => {
      observerRef.current?.disconnect()
      observerRef.current = null
    },
    [],
  )

  return (
    <Textarea
      {...props}
      ref={bindTextareaRef}
    />
  )
}

/** Fill-in-the-blank question stem — rich text with inline blank chips. */
export function FillBlankLeadInField({
  id = "qb-lead-in",
  value,
  onChange,
  disabled,
}: {
  id?: string
  value: string
  onChange: (value: string) => void
  disabled?: boolean
}) {
  return (
    <FillBlankRichTextEditor
      id={id}
      value={value}
      onChange={onChange}
      disabled={disabled}
      aria-required="true"
      aria-label="Question"
    />
  )
}

/**
 * Flattens a react-hook-form error tree into the messages an author can act on.
 * Zod issues land on nested paths (`options`, `references.0.url`, …), so a plain
 * `Object.values` misses most of them.
 */
export const MAX_BLOCKED_REASONS = 5
export function collectFormErrorMessages(errors: unknown): string[] {
  const found: string[] = []
  const walk = (node: unknown) => {
    if (!node || typeof node !== "object") return
    const message = (node as { message?: unknown }).message
    if (typeof message === "string" && message.trim()) found.push(message)
    for (const value of Object.values(node)) {
      if (value && typeof value === "object") walk(value)
    }
  }
  walk(errors)
  return Array.from(new Set(found)).slice(0, MAX_BLOCKED_REASONS)
}

// ─── Folder picker (Popover + Library secondary tree) ───────────────────────
//
// Compact selected tile — same visual rhythm as the collapsed
// "Question format" card. Popover uses `LibraryFolderPickerPanel` (search +
// folder tree aligned with the Library secondary panel).

const FOLDER_TINT_BG: Record<LibraryFolderColorKey, string> = {
  brand:
    "bg-[var(--icon-disc-brand-bg)] text-[var(--icon-disc-brand-fg)]",
  success:
    "bg-[var(--icon-disc-chart-2-bg)] text-[var(--icon-disc-chart-2-fg)]",
  warning:
    "bg-[var(--icon-disc-chart-4-bg)] text-[var(--icon-disc-chart-4-fg)]",
  destructive:
    "bg-[var(--icon-disc-danger-bg)] text-[var(--icon-disc-danger-fg)]",
  muted: "bg-muted text-muted-foreground",
  chart1:
    "bg-[color-mix(in_oklch,var(--color-chart-1)_22%,var(--muted))] text-[var(--color-chart-1)]",
  chart2: "bg-[var(--icon-disc-chart-2-bg)] text-[var(--icon-disc-chart-2-fg)]",
  chart3:
    "bg-[color-mix(in_oklch,var(--color-chart-3)_22%,var(--muted))] text-[var(--color-chart-3)]",
}

/** WCAG 1.4.11 icon-disc tokens only — four a11y pairings; reuse across formats. */
const QUESTION_FORMAT_ICON_TONE: Record<AuthoringQuestionType, TintedIconDiscTone> =
  {
    mcq_single: "brand",
    mcq_multiple: "chart-2",
    true_false: "chart-4",
    short_answer: "destructive",
    numeric: "brand",
    essay: "destructive",
    fill_blank: "chart-2",
    matching: "chart-4",
    ordering: "brand",
    hotspot: "chart-2",
  }

function questionFormatIconTileClass(
  type: AuthoringQuestionType,
  size: "trigger" | "list" = "trigger",
) {
  const tone = QUESTION_FORMAT_ICON_TONE[type] ?? "brand"
  return cn(
    "inline-flex shrink-0 items-center justify-center rounded-md hc:border hc:border-foreground forced-colors:border forced-colors:border-[CanvasText] forced-colors:bg-[Canvas] forced-colors:text-[CanvasText]",
    size === "trigger" ? "size-9" : "mt-0.5 size-8",
    ICON_DISC_TONE_CLASS[tone],
  )
}

interface FolderPickerControlProps {
  folders: LibraryFolder[]
  value: string[]
  onChange: (ids: string[]) => void
  open: boolean
  onOpenChange: (open: boolean) => void
  onRequestNewFolder: () => void
}

export function FolderPickerControl({
  folders,
  value,
  onChange,
  open,
  onOpenChange,
  onRequestNewFolder,
}: FolderPickerControlProps) {
  const selected = folders.filter((f) => value.includes(f.id))
  const primary = selected[0]
  const tint = primary
    ? FOLDER_TINT_BG[primary.colorKey]
    : FOLDER_TINT_BG.muted
  const locationLabel =
    selected.length === 0
      ? "Pick locations"
      : selected.length === 1
        ? primary!.name
        : `${selected.length} folders`
  const locationHint =
    selected.length === 0
      ? "Required — choose one or more"
      : selected.length === 1
        ? primary!.parentId
          ? `in ${folders.find((p) => p.id === primary!.parentId)?.name}`
          : "Top-level folder"
        : selected.map((f) => f.name).join(", ")

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={
            selected.length > 0
              ? `Change locations — currently ${selected
                  .map((f) => folderBreadcrumb(f.id, folders))
                  .join(", ")}`
              : "Pick locations"
          }
          className={INSPECTOR_PICKER_TRIGGER_CLASS}
        >
          <span
            className={cn(
              "inline-flex size-9 shrink-0 items-center justify-center rounded-md",
              tint,
            )}
            aria-hidden="true"
          >
            <i
              className={cn(
                "fa-light text-base",
                primary?.icon ?? "fa-folder"
              )}
            />
          </span>
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="truncate text-sm font-medium text-foreground">
              {locationLabel}
            </span>
            <span className="truncate text-xs text-muted-foreground">
              {locationHint}
            </span>
          </span>
          <span
            className="inline-flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors group-hover:bg-muted group-hover:text-foreground"
            aria-hidden="true"
          >
            <i className="fa-light fa-chevron-down text-xs" />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="bottom"
        sideOffset={6}
        collisionPadding={16}
        sticky="always"
        className={cn(
          "w-[var(--radix-popover-trigger-width)] min-w-[280px] p-0",
          // Prefer bottom; when Radix flips, shrink to the collision box so
          // “Folders” is not clipped by the viewport / inspector edge.
          "max-h-[min(28rem,var(--radix-popover-content-available-height))] overflow-hidden",
        )}
      >
        <LibraryFolderPickerPanel
          key={open ? "open" : "closed"}
          folders={folders}
          value={value}
          multiple
          onChange={onChange}
          onRequestNewFolder={onRequestNewFolder}
        />
      </PopoverContent>
    </Popover>
  )
}

interface FormatPickerControlProps {
  options: readonly AuthoringQuestionTypeOption[]
  value: AuthoringQuestionType
  onChange: (next: AuthoringQuestionType) => void
  disabled?: boolean
  id?: string
}

export function FormatPickerControl({
  options,
  value,
  onChange,
  disabled,
  id = "qb-format",
}: FormatPickerControlProps) {
  const [open, setOpen] = React.useState(false)
  const selected = authoringQuestionType(value)

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (disabled) return
        setOpen(next)
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={`Question format: ${selected.label}`}
          className={cn(
            INSPECTOR_PICKER_TRIGGER_CLASS,
            disabled && "cursor-not-allowed opacity-60",
          )}
        >
          <span
            className={questionFormatIconTileClass(value, "trigger")}
            aria-hidden="true"
          >
            <i className={cn("fa-light text-base", selected.icon)} />
          </span>
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="truncate text-sm font-medium text-foreground">
              {selected.shortLabel}
            </span>
            <span className="truncate text-xs text-muted-foreground">
              {selected.tileSummary}
            </span>
          </span>
          <span
            className="inline-flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors group-hover:bg-muted group-hover:text-foreground"
            aria-hidden="true"
          >
            <i className="fa-light fa-chevron-down text-xs" />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="bottom"
        sideOffset={6}
        collisionPadding={16}
        className="w-[var(--radix-popover-trigger-width)] min-w-[280px] p-1"
      >
        <div
          role="listbox"
          aria-label="Question format"
          className="max-h-[min(22rem,var(--radix-popover-content-available-height))] overflow-y-auto"
        >
          {options.map((option) => {
            const isSelected = option.value === value
            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={isSelected}
                className={cn(
                  "flex w-full items-start gap-3 rounded-md px-2.5 py-2 text-left transition-colors",
                  isSelected ? "bg-muted/70 text-foreground" : "hover:bg-muted/60",
                )}
                onClick={() => {
                  onChange(option.value)
                  setOpen(false)
                }}
              >
                <span
                  className={questionFormatIconTileClass(option.value, "list")}
                  aria-hidden="true"
                >
                  <i className={cn("fa-light text-sm", option.icon)} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    <span className="text-sm font-medium">{option.shortLabel}</span>
                    {isSelected ? (
                      <i
                        className="fa-solid fa-check text-xs text-foreground"
                        aria-hidden="true"
                      />
                    ) : null}
                  </span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">
                    {option.tileSummary}
                  </span>
                </span>
              </button>
            )
          })}
        </div>
      </PopoverContent>
    </Popover>
  )
}

// ─── Difficulty and discrimination ───────────────────────────────────────────
//
// Two different facts, which this card used to fuse under one "Predicted
// difficulty" heading with a bare PBI number underneath:
//
//   Difficulty     — how hard examinees find the item. Leo predicts it from the
//                    content (stem length, option count, Bloom's level,
//                    vocabulary); the author can override to Manual.
//   Discrimination — how well the item separates strong from weak examinees,
//                    reported as a PBI band. It comes from exam history, so the
//                    author cannot set it, and a new item simply has none.
//
// Bands come from `lib/pbi-bands` so this agrees with the Mix tab.

interface DifficultyMeterProps {
  value: "easy" | "medium" | "hard"
  onChange: (next: "easy" | "medium" | "hard") => void
  insight: ReturnType<typeof difficultyInsightForFolder>
  bloom?: string
  onBloomChange?: (next: LibraryBloomLevel) => void
  bloomMark?: (control: React.ReactNode) => React.ReactNode
  difficultyMark?: (control: React.ReactNode) => React.ReactNode
  include?: {
    difficulty?: boolean
    discrimination?: boolean
    bloom?: boolean
  }
}

const DIFFICULTY_CHIP_OPTIONS = AUTHORING_DIFFICULTY_OPTIONS.map((d) => ({
  value: d.value,
  label: d.label,
}))

const BLOOM_CHIP_OPTIONS = AUTHORING_BLOOM_OPTIONS.map((b) => ({
  value: b.value,
  label: b.label,
}))

export function DifficultyMeter({
  value,
  onChange,
  insight,
  bloom,
  onBloomChange,
  bloomMark,
  difficultyMark,
  include,
}: DifficultyMeterProps) {
  // The meter runs 0–0.60: PBI rarely clears 0.6 in practice, so scaling to 1.0
  // left every real item hugging the left third of the track.
  const showDifficulty = include?.difficulty !== false
  const showDiscrimination = include?.discrimination !== false
  const showBloom = include?.bloom !== false && Boolean(onBloomChange)
  const pbiPercent = Math.min(100, Math.max(0, (insight.pbi / 0.6) * 100))
  const band = pbiBand(insight.pbi)

  return (
    <div className="flex flex-col gap-4">
      {showDifficulty ? (
      <div className="flex flex-col gap-2">
        <InspectorSubsectionLabel>Difficulty</InspectorSubsectionLabel>
        {(() => {
          const chips = (
            <FilterChipGroup
              value={value}
              onValueChange={(v) => onChange(v as "easy" | "medium" | "hard")}
              options={DIFFICULTY_CHIP_OPTIONS}
              variant="brand"
              size="sm"
              aria-label="Difficulty"
            />
          )
          return difficultyMark ? difficultyMark(chips) : chips
        })()}
      </div>
      ) : null}

      {showDiscrimination ? (
      <div className="flex flex-col gap-1.5">
        <div className="flex items-baseline justify-between gap-2">
          <Tip side="top" label={PBI_EXPLAINER}>
            <span
              tabIndex={0}
              role="img"
              aria-label={`Discrimination — ${band.label}. ${PBI_EXPLAINER}`}
              className="inline-flex min-h-6 items-center gap-1.5"
            >
              <span className={COMPOSER_FIELD_LABEL_CLASS}>Discrimination</span>
              <i
                className="fa-light fa-circle-info text-xs text-muted-foreground"
                aria-hidden="true"
              />
            </span>
          </Tip>
          <span className="text-sm font-medium text-foreground">
            {band.label}
          </span>
        </div>
        <div className="h-1.5 rounded-full bg-muted">
          <div
            className={cn(
              "h-full rounded-full transition-[width] duration-200",
              band.meterClassName,
            )}
            style={{ width: `${pbiPercent}%` }}
          />
        </div>
        <p className="text-end text-xs text-muted-foreground">
          PBI{" "}
          <span className="font-mono font-medium text-foreground tabular-nums">
            {insight.pbi.toFixed(2)}
          </span>
        </p>
      </div>
      ) : null}

      {showBloom && onBloomChange ? (
        <div className="flex flex-col gap-2">
          <InspectorSubsectionLabel>Bloom&apos;s Taxonomy</InspectorSubsectionLabel>
          {(() => {
            const chips = (
              <FilterChipGroup
                value={bloom ?? ""}
                onValueChange={(v) => onBloomChange(v as LibraryBloomLevel)}
                options={BLOOM_CHIP_OPTIONS}
                variant="brand"
                size="sm"
                aria-label="Bloom taxonomy level"
              />
            )
            return bloomMark ? bloomMark(chips) : chips
          })()}
        </div>
      ) : null}
    </div>
  )
}
// ─────────────────────────────────────────────────────────────────────────────
// Option row — grouped list: letter chip marks correct, ⋯ menu for comment/remove.
// ─────────────────────────────────────────────────────────────────────────────

function OptionTextField({
  letter,
  optionNumber,
  value,
  showSuggestion,
  onTextChange,
  onAcceptSuggestion,
  onEnhanceSelection,
}: {
  letter: string
  optionNumber: number
  value: string
  showSuggestion: boolean
  onTextChange: (text: string) => void
  onAcceptSuggestion: () => void
  onEnhanceSelection?: (text: string) => void
}) {
  const inputRef = React.useRef<HTMLInputElement>(null)

  return (
    <>
      <Input
        ref={inputRef}
        value={value}
        onChange={(event) => onTextChange(event.target.value)}
        onKeyDown={(event) => {
          if (!showSuggestion || event.key !== "Tab" || event.shiftKey) return
          event.preventDefault()
          onAcceptSuggestion()
        }}
        placeholder={showSuggestion ? " " : `Option ${optionNumber}`}
        aria-label={`Option ${letter} text`}
        className={cn(
          BUILDER_LIST_FIELD,
          "relative z-[1] bg-transparent",
          showSuggestion && "placeholder:text-transparent",
        )}
      />
      {onEnhanceSelection ? (
        <ComposerFieldSelectionAsk
          inputRef={inputRef}
          ariaLabel={`Enhance option ${letter} selection with Leo`}
          onAsk={onEnhanceSelection}
        />
      ) : null}
    </>
  )
}

interface OptionRowProps {
  letter: string
  optionNumber: number
  option: { id: string; text: string; isCorrect: boolean; rationale: string }
  locked: boolean
  isMulti: boolean
  canRemove: boolean
  /** Stem-based suggestion — placeholder only; Tab accepts into the field. */
  suggestion?: string
  onEnhanceSelection?: (text: string) => void
  pinned?: boolean
  canPin?: boolean
  onTogglePin?: () => void
  onTextChange: (t: string) => void
  onSelectLetter: () => void
  onToggleCorrect: () => void
  onRationaleChange: (t: string) => void
  onRemove: () => void
}

export function OptionRow({
  letter,
  optionNumber,
  option,
  locked,
  isMulti,
  canRemove,
  suggestion = "",
  onEnhanceSelection,
  pinned = false,
  canPin = false,
  onTogglePin,
  onTextChange,
  onSelectLetter,
  onToggleCorrect,
  onRationaleChange,
  onRemove,
}: OptionRowProps) {
  const [rationaleOpen, setRationaleOpen] = React.useState(
    option.rationale.length > 0
  )
  const showSuggestion =
    !locked && suggestion.trim().length > 0 && option.text.trim().length === 0

  function acceptSuggestion() {
    if (!showSuggestion) return
    onTextChange(suggestion)
  }

  return (
    <div className="space-y-2">
      <BuilderListRow highlighted={option.isCorrect}>
        <Tip
          side="top"
          label={
            option.isCorrect
              ? isMulti
                ? `Option ${letter} is correct — click to unmark`
                : `Option ${letter} is the correct answer`
              : `Mark option ${letter} as correct`
          }
        >
          <BuilderAnswerChip
            letter={letter}
            correct={option.isCorrect}
            onClick={onSelectLetter}
            ariaLabel={
              option.isCorrect
                ? isMulti
                  ? `Option ${letter}, marked correct`
                  : `Option ${letter}, correct answer`
                : `Mark option ${letter} as correct`
            }
          />
        </Tip>

        {locked ? (
          <div
            className={BUILDER_LIST_LOCKED}
            aria-label={`Option ${letter} text, fixed label`}
          >
            {option.text}
          </div>
        ) : (
          <div className="relative min-w-0 flex-1">
            {showSuggestion ? (
              <div
                aria-hidden="true"
                className={cn(
                  "pointer-events-none absolute inset-0 z-0 flex min-w-0 items-center gap-2",
                  "text-base font-medium text-muted-foreground md:text-lg"
                )}
              >
                <span className="min-w-0 flex-1 truncate">{suggestion}</span>
                <span className="inline-flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
                  <Kbd variant="bare">Tab</Kbd>
                </span>
              </div>
            ) : null}
            <OptionTextField
              letter={letter}
              optionNumber={optionNumber}
              value={option.text}
              showSuggestion={showSuggestion}
              onTextChange={onTextChange}
              onAcceptSuggestion={acceptSuggestion}
              onEnhanceSelection={onEnhanceSelection}
            />
          </div>
        )}

        {!locked ? (
          <BuilderListRowActions>
            <DropdownMenu>
              <Tip side="top" label="More actions">
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    className="icon-button-chrome"
                    aria-label={`More actions for option ${letter}`}
                  >
                    <i className="fa-light fa-ellipsis" aria-hidden="true" />
                  </Button>
                </DropdownMenuTrigger>
              </Tip>
              <DropdownMenuContent align="end" className="min-w-44">
                <DropdownMenuItem onSelect={() => onToggleCorrect()}>
                  <i
                    className={cn(
                      "fa-light",
                      option.isCorrect ? "fa-circle-xmark" : "fa-circle-check"
                    )}
                    aria-hidden="true"
                  />
                  {option.isCorrect ? "Unmark correct" : "Mark correct"}
                </DropdownMenuItem>
                {canPin && onTogglePin ? (
                  <DropdownMenuItem onSelect={() => onTogglePin()}>
                    <i
                      className="fa-light fa-thumbtack"
                      aria-hidden="true"
                    />
                    {pinned ? "Unpin option" : "Pin this option"}
                  </DropdownMenuItem>
                ) : null}
                <DropdownMenuItem
                  onSelect={() => setRationaleOpen((open) => !open)}
                >
                  <i
                    className={cn(
                      "fa-light",
                      rationaleOpen ? "fa-message-slash" : "fa-message-lines"
                    )}
                    aria-hidden="true"
                  />
                  {rationaleOpen ? "Hide comment" : "Add comment"}
                </DropdownMenuItem>
                {canRemove ? (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      variant="destructive"
                      onSelect={() => onRemove()}
                    >
                      <i className="fa-light fa-trash-can" aria-hidden="true" />
                      Remove option
                    </DropdownMenuItem>
                  </>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          </BuilderListRowActions>
        ) : null}
      </BuilderListRow>

      {rationaleOpen && !locked ? (
        <div
          className={cn(
            BUILDER_LIST_ROW,
            "flex-col items-stretch gap-2 py-2.5 ps-11"
          )}
        >
          <Textarea
            value={option.rationale}
            onChange={(e) => onRationaleChange(e.target.value)}
            placeholder={
              option.isCorrect
                ? "Why this is the single best answer (mechanism, guideline)…"
                : "Why this distractor is plausible but wrong (common confusion)…"
            }
            rows={2}
            className="min-h-[64px] resize-y border-0 bg-transparent text-sm shadow-none focus-visible:ring-0"
            aria-label={`Option ${letter} rationale`}
          />
        </div>
      ) : null}
    </div>
  )
}

export function revokeAuthoringReferencePreview(ref: AuthoringReference) {
  if (ref.previewUrl.startsWith("blob:")) {
    URL.revokeObjectURL(ref.previewUrl)
  }
}

function referenceKindMeta(kind: AuthoringReferenceKind) {
  return (
    AUTHORING_REFERENCE_KIND_OPTIONS.find((option) => option.value === kind) ??
    AUTHORING_REFERENCE_KIND_OPTIONS[0]
  )
}

interface ReferenceRowProps {
  index: number
  reference: AuthoringReference
  onPatch: (patch: Partial<AuthoringReference>) => void
  onRemove: () => void
  onChangeKind: (kind: AuthoringReferenceKind) => void
  onPickFile: (kind: "image" | "document") => void
}

export function ReferenceRow({
  index,
  reference,
  onPatch,
  onRemove,
  onChangeKind,
  onPickFile,
}: ReferenceRowProps) {
  const kindMeta = referenceKindMeta(reference.kind)
  const hasStudentPreview =
    (reference.kind === "image" && reference.previewUrl.length > 0) ||
    (reference.kind === "document" && reference.fileName.trim().length > 0) ||
    (reference.kind === "link" && reference.url.trim().length > 0)

  return (
    <div className="space-y-2">
      <BuilderListRow>
        <span className={builderListIndexClass(7)} aria-hidden="true">
          {index}
        </span>

        <Tip side="top" label={kindMeta.description}>
          <span
            tabIndex={0}
            role="img"
            aria-label={`${kindMeta.label} reference`}
            className={cn(
              builderListIndexClass(7),
              "border-dashed text-muted-foreground"
            )}
          >
            <i
              className={cn("fa-light text-xs", kindMeta.icon)}
              aria-hidden="true"
            />
          </span>
        </Tip>

        {reference.kind === "citation" ? (
          <Input
            value={reference.citation}
            onChange={(e) => onPatch({ citation: e.target.value })}
            placeholder="Harrison's Internal Medicine, 21st ed., ch. 269"
            aria-label={`Reference ${index} citation`}
            className={BUILDER_LIST_FIELD}
          />
        ) : null}

        {reference.kind === "link" ? (
          <Input
            value={reference.url}
            onChange={(e) => onPatch({ url: e.target.value })}
            placeholder="https://…"
            inputMode="url"
            aria-label={`Reference ${index} link URL`}
            className={BUILDER_LIST_FIELD}
          />
        ) : null}

        {reference.kind === "image" || reference.kind === "document" ? (
          reference.fileName.trim() ? (
            <div
              className={cn(BUILDER_LIST_LOCKED, "truncate text-foreground")}
              title={reference.fileName}
            >
              {reference.fileName}
            </div>
          ) : (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-11 min-w-0 flex-1 justify-start truncate"
              onClick={() =>
                onPickFile(reference.kind === "image" ? "image" : "document")
              }
            >
              <i className={cn("fa-light", kindMeta.icon)} aria-hidden="true" />
              Choose {reference.kind === "image" ? "image" : "document"}
            </Button>
          )
        ) : null}

        <BuilderListRowActions>
          {reference.kind === "image" || reference.kind === "document" ? (
            reference.fileName.trim() ? (
              <Tip side="top" label={`Replace ${reference.kind}`}>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  className="icon-button-chrome"
                  onClick={() =>
                    onPickFile(
                      reference.kind === "image" ? "image" : "document"
                    )
                  }
                  aria-label={`Replace reference ${index} ${reference.kind}`}
                >
                  <i
                    className="fa-light fa-arrow-rotate-right"
                    aria-hidden="true"
                  />
                </Button>
              </Tip>
            ) : null
          ) : null}
          <DropdownMenu>
            <Tip side="top" label="More actions">
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  className="icon-button-chrome"
                  aria-label={`More actions for reference ${index}`}
                >
                  <i className="fa-light fa-ellipsis" aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>
            </Tip>
            <DropdownMenuContent align="end" className="min-w-48">
              {AUTHORING_REFERENCE_KIND_OPTIONS.map((option) => (
                <DropdownMenuItem
                  key={option.value}
                  onSelect={() => onChangeKind(option.value)}
                >
                  <i
                    className={cn("fa-light", option.icon)}
                    aria-hidden="true"
                  />
                  {option.label}
                  {reference.kind === option.value ? (
                    <i
                      className="fa-solid fa-check ms-auto text-brand"
                      aria-hidden="true"
                    />
                  ) : null}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                variant="destructive"
                onSelect={() => onRemove()}
              >
                <i className="fa-light fa-trash-can" aria-hidden="true" />
                Remove reference
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </BuilderListRowActions>
      </BuilderListRow>

      {hasStudentPreview ? (
        <ReferenceStudentPreview
          index={index}
          reference={reference}
          onPatch={onPatch}
          onPickFile={onPickFile}
        />
      ) : null}
    </div>
  )
}

function ReferenceStudentPreview({
  index,
  reference,
  onPatch,
  onPickFile,
}: {
  index: number
  reference: AuthoringReference
  onPatch: (patch: Partial<AuthoringReference>) => void
  onPickFile: (kind: "image" | "document") => void
}) {
  return (
    <div
      className={cn(
        BUILDER_LIST_ROW,
        "ms-0 flex-col items-stretch gap-3 border-dashed py-3 ps-3 sm:ps-4"
      )}
    >
      <p className="text-xs font-medium text-muted-foreground">
        Students see this while answering
      </p>

      {reference.kind === "image" && reference.previewUrl ? (
        <img
          src={reference.previewUrl}
          alt={
            reference.linkLabel.trim() ||
            reference.fileName ||
            `Reference figure ${index}`
          }
          className="max-h-56 w-full rounded-md border border-border bg-muted/30 object-contain"
        />
      ) : null}

      {reference.kind === "document" && reference.fileName ? (
        <div className="flex items-center gap-3 rounded-md border border-border bg-background px-3 py-2.5">
          <span
            className="inline-flex size-10 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground"
            aria-hidden="true"
          >
            <i className="fa-light fa-file-lines text-lg" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-foreground">
              {reference.fileName}
            </p>
            <p className="text-xs text-muted-foreground">Document attachment</p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onPickFile("document")}
          >
            Replace
          </Button>
        </div>
      ) : null}

      {reference.kind === "link" && reference.url.trim() ? (
        <a
          href={reference.url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-3 rounded-md border border-border bg-background px-3 py-2.5 text-sm font-medium text-foreground underline underline-offset-4"
        >
          <span
            className="inline-flex size-10 shrink-0 items-center justify-center rounded-md bg-brand-tint text-brand-deep"
            aria-hidden="true"
          >
            <i className="fa-light fa-arrow-up-right-from-square" />
          </span>
          <span className="min-w-0 flex-1 truncate">
            {reference.linkLabel.trim() || reference.url}
          </span>
        </a>
      ) : null}

      {reference.kind === "link" ||
      reference.kind === "image" ||
      reference.kind === "document" ? (
        <Input
          value={reference.linkLabel}
          onChange={(e) => onPatch({ linkLabel: e.target.value })}
          placeholder="Optional caption for students"
          aria-label={`Reference ${index} student caption`}
          className="h-9 text-sm"
        />
      ) : null}
    </div>
  )
}
